<template>
  <v-card class="ma-0 pa-0">
    <!-- PDF viewer -->
    <div class="pdf-viewer-wrapper">
      <PdfControls
        :current-page="currentPage"
        :total-pages="totalPages"
        :scale="scale"
        :search-available="isSearchAvailable"
        @page-change="handlePageChange"
        @scale-change="handleScaleChange"
        @search="handleSearch"
        @search-clear="handleSearchClear"
        @fit-to-viewport="handleFitToViewport"
      />

      <div class="pdf-content-area">
        <PdfSidebar
          v-if="showSidebar && brochures.length > 1"
          :pdf-list="brochures"
          :current-pdf="selectedBrochure"
          :loading="loadingPdf"
          :loading-selection="loadingBrochureSelection"
          @select-pdf="selectBrochure"
          @pre-select="handlePreSelect"
        />

        <PdfViewer
          ref="pdfViewer"
          :pdf-url="pdfUrl"
          :scale="scale"
          :page-number="currentPage"
          :watermark-config="watermarkConfig"
          :user-name="user.userName"
          :user-full-name="user.fullName"
          :loading-brochures="loadingBrochures"
          :loading-pdf="loadingPdf"
          :loading-selection="loadingBrochureSelection"
          @pdf-loaded="handlePdfLoaded"
          @pdf-error="handlePdfError"
          @page-rendered="handlePageRendered"
          @scale-change="handleScaleChange"
        />

        <!-- Search Results Overlay -->
        <div v-if="searchResults.length > 0" class="search-results-overlay">
          <v-card class="pa-2" elevation="3">
            <div class="d-flex align-center">
              <span class="text-caption mr-2">
                Search Results: {{ currentSearchIndex + 1 }} of
                {{ searchResults.length }}
              </span>
              <v-btn
                icon
                x-small
                @click="goToPreviousSearchResult"
                :disabled="searchResults.length <= 1"
              >
                <v-icon x-small>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                @click="goToNextSearchResult"
                :disabled="searchResults.length <= 1"
              >
                <v-icon x-small>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn icon x-small @click="handleSearchClear" class="ml-1">
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
            <div
              class="text-caption grey--text"
              v-if="searchResults[currentSearchIndex]"
            >
              Code: {{ searchResults[currentSearchIndex].code }} | Page:
              {{ searchResults[currentSearchIndex].page }}
            </div>
          </v-card>
        </div>
      </div>
    </div>

    <!-- Search Results Dialog -->
    <v-dialog v-model="searchDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon
            :color="
              searchDialogType === 'error'
                ? 'error'
                : searchDialogType === 'warning'
                ? 'warning'
                : 'info'
            "
            class="mr-2"
          >
            {{
              searchDialogType === "error"
                ? "mdi-alert-circle"
                : searchDialogType === "warning"
                ? "mdi-alert"
                : "mdi-information"
            }}
          </v-icon>
          {{ searchDialogTitle }}
        </v-card-title>

        <v-card-text>
          <p class="mb-3">{{ searchDialogMessage }}</p>

          <!-- Additional info for no results -->
          <div
            v-if="searchDialogType === 'info'"
            class="grey lighten-4 pa-3 rounded"
          >
            <div class="text-caption grey--text text--darken-2 mb-2">
              <strong>Consejos de búsqueda:</strong>
            </div>
            <ul
              class="text-caption grey--text text--darken-1 ma-0 pa-0"
              style="list-style-position: inside"
            >
              <li>Revisa la ortografía del código</li>
              <li>Intenta con códigos más cortos</li>
              <li>Usa solo números o letras del código</li>
              <li>Verifica que el código esté en este catálogo</li>
            </ul>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="searchDialog = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import loginService from "../services/login.service";
import userService from "../services/user.service";
import catalogService from "../services/catalog.service";
import fileService from "../services/file.service";
import PdfViewer from "./components/PdfViewer.vue";
import PdfControls from "./components/PdfControls.vue";
import PdfSidebar from "./components/PdfSidebar.vue";
import pdfSecurityUtils from "../utils/pdfSecurity";

export default {
  name: "BookView",
  components: {
    PdfViewer,
    PdfControls,
    PdfSidebar,
  },
  data: () => ({
    company: {},
    user: {},
    brochures: [],
    selectedBrochure: null,

    // PDF viewer state
    currentPage: 1,
    totalPages: 1,
    scale: 1,
    pdfUrl: null,
    loadingPdf: true, // Start with loading = true immediately
    loadingBrochures: true, // Track brochure list loading separately
    loadingBrochureSelection: false, // Track individual brochure selection loading
    loadingStartTime: Date.now(), // Track loading start time
    loadingTimer: null, // Timer for elapsed time display
    pdfLoadTimeout: null,
    showSidebar: true,
    watermarkConfig: null,

    // Metadata and search
    brochureMetadata: null,
    searchResults: [],
    currentSearchIndex: -1,

    // Search dialog
    searchDialog: false,
    searchDialogTitle: "",
    searchDialogMessage: "",
    searchDialogType: "info", // info, warning, error
  }),
  computed: {
    isSearchAvailable() {
      return (
        this.selectedBrochure &&
        this.selectedBrochure.idFileMetadata &&
        this.brochureMetadata &&
        this.brochureMetadata.codes &&
        Array.isArray(this.brochureMetadata.codes)
      );
    },
  },
  watch: {
    selectedBrochure(newVal) {
      if (newVal) {
        // Ensure loading state is active (should already be from selectBrochure)
        // This double-ensures immediate loading feedback
        if (!this.loadingPdf) {
          this.loadingPdf = true;
          this.loadingBrochureSelection = true;
        }
        console.log("Brochure selected, starting loading:", newVal.idBrochure);
        this.loadPdfForBrochure(newVal);
      }
    },
    pdfUrl(newVal, oldVal) {
      console.log("PDF URL changed:", { oldVal, newVal });
      if (newVal) {
        console.log(
          "New PDF URL type:",
          newVal.startsWith("blob:") ? "blob" : "regular"
        );
        // Force a small delay to ensure the PDF viewer has time to process the URL change
        this.$nextTick(() => {
          console.log("Vue nextTick after pdfUrl change");
        });
      }
    },
  },
  methods: {
    ...mapActions(["hideOopControls", "setBrochureMetadata"]),

    loadUserData() {
      this.user = loginService.getUserData();
    },

    getCompany() {
      userService.getCompany().then((d) => {
        this.company = d.company;
        this.updateWatermarkConfig();
      });
    },

    getBrochures() {
      console.log("Starting to fetch brochures list...");
      // Both loading states should be true during brochure fetching
      this.loadingBrochures = true;
      this.loadingPdf = true;

      userService
        .getBrochures()
        .then((d) => {
          console.log("Brochures fetched:", d.length);
          this.brochures = d;
          this.loadingBrochures = false; // Brochure list loading is complete

          if (this.brochures.length > 0) {
            // The watcher will handle loading the PDF
            this.selectedBrochure = this.brochures[0];
          } else {
            // No brochures available, stop all loading
            this.loadingPdf = false;
            console.warn("No brochures available for this user");
          }
        })
        .catch((error) => {
          console.error("Failed to fetch brochures:", error);
          this.loadingBrochures = false;
          this.loadingPdf = false;

          // Show error message to user
          this.showSearchDialog(
            "Error",
            "Failed to load brochures. Please try refreshing the page.",
            "error"
          );
        });
    },

    getBrochureMetadata(idBrochure) {
      if (!idBrochure) return;

      // Check if this brochure has metadata
      const brochure = this.brochures.find((b) => b.idBrochure === idBrochure);
      if (!brochure || !brochure.idFileMetadata) {
        console.log("No metadata available for this brochure");
        this.brochureMetadata = null;
        return;
      }

      fileService
        .getBrochureMetadata(idBrochure)
        .then((metadata) => {
          this.brochureMetadata = metadata;
          // Update store with metadata
          this.setBrochureMetadata(metadata);
          console.log("Brochure metadata loaded:", metadata);
        })
        .catch((error) => {
          console.error("Error loading brochure metadata:", error);
          this.brochureMetadata = null;
          this.setBrochureMetadata(null);
        });
    },

    setCookie(name, value, minutes) {
      const date = new Date();
      date.setTime(date.getTime() + minutes * 60 * 1000);
      const expires = `expires=${date.toUTCString()}`;
      document.cookie = `${name}=${encodeURIComponent(
        value
      )}; ${expires}; path=/`;
    },

    deleteCookie(name) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    },

    // PDF viewer methods
    async loadPdfForBrochure(brochure) {
      // Loading state should already be active from selectBrochure/handlePreSelect
      // But ensure it's set as a failsafe
      if (!this.loadingPdf) {
        this.loadingPdf = true;
        this.loadingBrochureSelection = true;
      }
      console.log("Starting PDF load for brochure:", brochure.idBrochure);

      this.cleanupPdfUrl();

      try {
        // Get PDF URL from the brochure (now async and returns decrypted blob URL)
        console.log("Calling renderBrochure...");
        const pdfUrl = await catalogService.renderBrochure(brochure);
        console.log("PDF URL received:", pdfUrl);

        this.pdfUrl = pdfUrl;

        // Reset page to 1 when loading new PDF
        this.currentPage = 1;

        // Load metadata for this brochure
        this.getBrochureMetadata(brochure.idBrochure);

        // Update watermark config for this brochure
        this.updateWatermarkConfig();

        // Set a timeout to reset loading state if the PDF viewer doesn't respond
        // This prevents the loading state from getting stuck
        this.pdfLoadTimeout = setTimeout(() => {
          if (this.loadingPdf) {
            console.warn("PDF loading timeout - forcing loadingPdf to false");
            this.loadingPdf = false;
          }
        }, 60000); // 60 second timeout

        console.log(
          "PDF load process completed, waiting for viewer to load..."
        );
      } catch (error) {
        console.error("Failed to load PDF:", error);
        this.loadingPdf = false;

        // Show user-friendly error message
        this.showSearchDialog(
          "Error al cargar PDF",
          `Failed to decrypt and load PDF: ${error.message}`,
          "error"
        );
      }
    },

    updateWatermarkConfig() {
      const userData = {
        userName: this.user.userName || "",
        fullName: this.user.fullName || this.user.name || "",
        email: this.user.email || "",
        id: this.user.id || this.user.userId || "",
      };

      this.watermarkConfig = pdfSecurityUtils.getWatermarkConfig(userData);
    },

    cleanupPdfUrl() {
      // Clean up previous blob URL to prevent memory leaks
      if (this.pdfUrl && this.pdfUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.pdfUrl);
      }
    },

    handlePdfLoaded(event) {
      console.log("PDF loaded successfully, pages:", event.numPages);
      this.totalPages = event.numPages;
      this.loadingPdf = false;
      this.loadingBrochureSelection = false; // Clear brochure selection loading

      // Clear the timeout since PDF loaded successfully
      if (this.pdfLoadTimeout) {
        clearTimeout(this.pdfLoadTimeout);
        this.pdfLoadTimeout = null;
      }
    },

    handlePdfError(event) {
      console.error("PDF loading error:", event);
      this.loadingPdf = false;
      this.loadingBrochureSelection = false; // Clear brochure selection loading

      // Clear the timeout since we got an error
      if (this.pdfLoadTimeout) {
        clearTimeout(this.pdfLoadTimeout);
        this.pdfLoadTimeout = null;
      }

      this.showSearchDialog(
        "Error al cargar PDF",
        `Error loading PDF: ${event.error || "Unknown error"}`,
        "error"
      );
    },

    handlePageRendered(event) {
      console.log(`Page ${event.pageNumber} rendered`);
    },

    // Navigation from TopNav headers
    navigateToPage(pageNumber) {
      if (pageNumber >= 1 && pageNumber <= this.totalPages) {
        this.currentPage = pageNumber;
        console.log(`Navigating to page ${pageNumber} from TopNav`);
      }
    },

    handlePageChange(newPage) {
      this.currentPage = newPage;
    },

    handleScaleChange(newScale) {
      this.scale = newScale;
    },

    handleFitToViewport() {
      if (this.$refs.pdfViewer) {
        this.$refs.pdfViewer.fitToViewport();
      }
    },

    handlePreSelect(brochure) {
      console.log(
        "⚡ Pre-selecting brochure (immediate loading):",
        brochure.title || brochure.name
      );

      // Start loading state immediately - even before selection logic
      this.loadingPdf = true;
      this.loadingBrochureSelection = true;

      console.log("🚀 Loading state activated immediately on pre-select");
    },

    async selectBrochure(brochure) {
      console.log(
        "🔄 Selecting brochure:",
        brochure.title || brochure.name,
        "ID:",
        brochure.idBrochure
      );

      // Start loading state immediately, before any other operations
      // This ensures the UI shows loading feedback as soon as selection is initiated
      this.loadingPdf = true;
      this.loadingBrochureSelection = true;

      // Add a small delay to ensure the UI updates with loading state
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Clear any previous search results and states
      this.searchResults = [];
      this.currentSearchIndex = -1;
      this.currentPage = 1;
      this.totalPages = 0;
      this.pdfUrl = null;

      // Set the selected brochure (this will trigger the watcher)
      this.selectedBrochure = brochure;

      console.log("✅ Brochure selection completed");
    },

    handleSearch(searchText) {
      this.searchForCode(searchText);
    },

    searchForCode(code) {
      if (!code || !code.trim()) {
        this.searchResults = [];
        this.currentSearchIndex = -1;
        return;
      }

      const trimmedCode = code.trim();

      // Check if metadata is available for code search
      if (!this.brochureMetadata) {
        // If no metadata and it's a number, try page navigation
        const pageNumber = parseInt(trimmedCode);
        if (!isNaN(pageNumber) && trimmedCode === pageNumber.toString()) {
          this.navigateToPageIfValid(pageNumber);
          return;
        }

        this.showSearchDialog(
          "Búsqueda no disponible",
          "La búsqueda no está disponible para este catálogo porque no se encontraron metadatos de búsqueda.",
          "warning"
        );
        this.searchResults = [];
        this.currentSearchIndex = -1;
        return;
      }

      if (
        !this.brochureMetadata.codes ||
        !Array.isArray(this.brochureMetadata.codes)
      ) {
        // If invalid metadata and it's a number, try page navigation
        const pageNumber = parseInt(trimmedCode);
        if (!isNaN(pageNumber) && trimmedCode === pageNumber.toString()) {
          this.navigateToPageIfValid(pageNumber);
          return;
        }

        this.showSearchDialog(
          "Error de metadatos",
          "La búsqueda no está disponible para este catálogo debido a un formato de metadatos inválido.",
          "error"
        );
        this.searchResults = [];
        this.currentSearchIndex = -1;
        return;
      }

      const results = this.brochureMetadata.codes
        .filter(
          (item) =>
            item.code &&
            item.code.toLowerCase().includes(trimmedCode.toLowerCase())
        )
        .map((item) => ({
          code: item.code,
          page: item.page,
        }))
        .sort((a, b) => a.page - b.page);

      if (results.length > 0) {
        this.searchResults = results;
        this.currentSearchIndex = 0;
        this.goToSearchResult(0);
        return;
      }

      const pageNumber = parseInt(trimmedCode);
      if (!isNaN(pageNumber) && trimmedCode === pageNumber.toString()) {
        this.navigateToPageIfValid(pageNumber);
        return;
      }

      this.searchResults = [];
      this.currentSearchIndex = -1;
      this.showSearchDialog(
        "Sin resultados",
        `No se encontraron resultados para "${trimmedCode}". El código podría no existir en este catálogo o estar escrito de forma diferente.`,
        "info"
      );
    },

    navigateToPageIfValid(pageNumber) {
      if (pageNumber >= 1 && pageNumber <= this.totalPages) {
        this.currentPage = pageNumber;
        this.searchResults = [];
        this.currentSearchIndex = -1;
        console.log(`Navigating directly to page ${pageNumber}`);
      } else {
        this.showSearchDialog(
          "Página no válida",
          `La página ${pageNumber} no existe. Este documento tiene ${this.totalPages} páginas.`,
          "warning"
        );
      }
    },

    goToSearchResult(index) {
      if (index >= 0 && index < this.searchResults.length) {
        this.currentSearchIndex = index;
        const result = this.searchResults[index];
        this.currentPage = result.page;

        console.log(
          `Navigating to page ${result.page} for code "${result.code}" (${
            index + 1
          }/${this.searchResults.length})`
        );
      }
    },

    handleSearchClear() {
      this.searchResults = [];
      this.currentSearchIndex = -1;
    },

    goToNextSearchResult() {
      if (this.searchResults.length > 0) {
        const nextIndex =
          (this.currentSearchIndex + 1) % this.searchResults.length;
        this.goToSearchResult(nextIndex);
      }
    },

    goToPreviousSearchResult() {
      if (this.searchResults.length > 0) {
        const prevIndex =
          this.currentSearchIndex > 0
            ? this.currentSearchIndex - 1
            : this.searchResults.length - 1;
        this.goToSearchResult(prevIndex);
      }
    },

    showSearchDialog(title, message, type = "info") {
      this.searchDialogTitle = title;
      this.searchDialogMessage = message;
      this.searchDialogType = type;
      this.searchDialog = true;
    },
  },
  mounted() {
    this.hideOopControls();
    this.loadUserData();
    this.getCompany();
    this.getBrochures();

    // Make navigateToPage globally accessible
    window.navigateToPage = this.navigateToPage;

    pdfSecurityUtils.initDocumentSecurity();

    this.$gtag.pageview({
      page_path: "/BookView.vue",
    });

    this.setCookie("token", loginService.getRawToken(), 30);
  },
  beforeDestroy() {
    this.deleteCookie("token");

    this.cleanupPdfUrl();

    if (this.pdfLoadTimeout) {
      clearTimeout(this.pdfLoadTimeout);
      this.pdfLoadTimeout = null;
    }

    if (window.navigateToPage) {
      delete window.navigateToPage;
    }

    pdfSecurityUtils.removeDocumentSecurity();
  },
};
</script>

<style>
/* PDF Viewer Styles */
.pdf-viewer-wrapper {
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
}

.pdf-content-area {
  flex: 1;
  display: flex;
  overflow: hidden;
  height: 100%;
}

/* Responsive design */
@media (max-width: 768px) {
  .pdf-content-area {
    flex-direction: column;
  }
}

/* Search results overlay */
.search-results-overlay {
  position: absolute;
  top: 70px;
  right: 20px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 200px;
  backdrop-filter: blur(5px);
}

.search-results-overlay .v-btn {
  margin: 0 2px;
}

.search-results-text {
  margin: 8px 0;
  font-size: 14px;
  color: #333;
}

/* Prevent printing */
@media print {
  .pdf-viewer-wrapper * {
    display: none !important;
    visibility: hidden !important;
  }

  body::before {
    content: "Printing is disabled for security reasons";
    display: block !important;
    visibility: visible !important;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 24px;
    color: #000;
    background: #fff;
    padding: 20px;
    border: 2px solid #000;
    z-index: 999999;
  }
}
</style>